const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  constructor() {
    this.baseUrl = `${API_URL}/api`;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'An error occurred');
      }
      
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Services
  async getServices() {
    return this.request('/services');
  }

  async getPackages() {
    return this.request('/packages');
  }

  async getAddons() {
    return this.request('/addons');
  }

  async checkAvailability(date) {
    return this.request(`/availability/${date}`);
  }

  // Bookings
  async createBooking(bookingData) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async getBooking(bookingId) {
    return this.request(`/bookings/${bookingId}`);
  }

  async getUserBookings(email) {
    return this.request(`/bookings/user/${email}`);
  }

  async cancelBooking(bookingId, email, reason = '') {
    return this.request(`/bookings/${bookingId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ customer_email: email, reason }),
    });
  }

  async rescheduleBooking(bookingId, email, newDate, newTime) {
    return this.request(`/bookings/${bookingId}/reschedule`, {
      method: 'POST',
      body: JSON.stringify({
        customer_email: email,
        new_date: newDate,
        new_time: newTime,
      }),
    });
  }

  // Payments
  async createPaymentIntent(bookingId, paymentType) {
    return this.request('/payments/create-intent', {
      method: 'POST',
      body: JSON.stringify({ booking_id: bookingId, payment_type: paymentType }),
    });
  }

  async confirmPayment(bookingId, paymentIntentId) {
    return this.request('/payments/confirm', {
      method: 'POST',
      body: JSON.stringify({
        booking_id: bookingId,
        payment_intent_id: paymentIntentId,
      }),
    });
  }

  // Promo Codes
  async validatePromoCode(code, totalAmount) {
    return this.request('/promo/validate', {
      method: 'POST',
      body: JSON.stringify({ code, total_amount: totalAmount }),
    });
  }

  // Reviews
  async getReviews(approvedOnly = true) {
    return this.request(`/reviews?approved_only=${approvedOnly}`);
  }

  async submitReview(reviewData) {
    return this.request('/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  // Gallery
  async getGallery() {
    return this.request('/gallery');
  }

  // Contact Form
  async submitContactForm(formData) {
    return this.request('/contact', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  // Chat
  async getChatRoom(userId) {
    return this.request(`/chat/rooms?user_id=${userId}`);
  }

  async sendMessage(roomId, senderId, senderType, message) {
    return this.request('/chat/send', {
      method: 'POST',
      body: JSON.stringify({
        room_id: roomId,
        sender_id: senderId,
        sender_type: senderType,
        message,
      }),
    });
  }

  async getChatHistory(roomId) {
    return this.request(`/chat/history/${roomId}`);
  }

  // Contract
  async signContract(contractData) {
    return this.request('/contract/sign', {
      method: 'POST',
      body: JSON.stringify(contractData),
    });
  }

  // Admin
  async adminLogin(email, password) {
    return this.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async checkAdmin(email) {
    return this.request(`/admin/check?email=${email}`);
  }

  async getAdminDashboard(adminEmail) {
    return this.request(`/admin/dashboard?admin_email=${adminEmail}`);
  }

  async getAdminBookings(adminEmail, status = null) {
    const statusParam = status ? `&status=${status}` : '';
    return this.request(`/admin/bookings?admin_email=${adminEmail}${statusParam}`);
  }

  async updateBookingStatus(bookingId, adminEmail, status) {
    return this.request(`/admin/bookings/${bookingId}/status?admin_email=${adminEmail}&status=${status}`, {
      method: 'PUT',
    });
  }

  async getCustomerStats(adminEmail) {
    return this.request(`/admin/customers?admin_email=${adminEmail}`);
  }

  async getCalendarEvents(adminEmail, month = null) {
    const monthParam = month ? `&month=${month}` : '';
    return this.request(`/admin/calendar-events?admin_email=${adminEmail}${monthParam}`);
  }

  async sendBulkNotification(adminEmail, title, body, filter = 'all') {
    return this.request(`/admin/send-bulk-notification?admin_email=${adminEmail}&title=${encodeURIComponent(title)}&body=${encodeURIComponent(body)}&recipient_filter=${filter}`, {
      method: 'POST',
    });
  }

  // Admin - Promo Codes
  async getPromoCodes(adminEmail) {
    return this.request(`/promo/all?admin_email=${adminEmail}`);
  }

  async createPromoCode(adminEmail, promoData) {
    return this.request(`/promo?admin_email=${adminEmail}`, {
      method: 'POST',
      body: JSON.stringify(promoData),
    });
  }

  async deletePromoCode(codeId, adminEmail) {
    return this.request(`/promo/${codeId}?admin_email=${adminEmail}`, {
      method: 'DELETE',
    });
  }

  // Admin - Reviews
  async getAdminReviews(adminEmail) {
    return this.request(`/reviews?approved_only=false`);
  }

  async approveReview(reviewId, approved = true) {
    return this.request(`/reviews/${reviewId}/approve?approved=${approved}`, {
      method: 'PUT',
    });
  }

  async deleteReview(reviewId) {
    return this.request(`/reviews/${reviewId}`, {
      method: 'DELETE',
    });
  }

  // Admin - Gallery
  async addGalleryItem(itemData) {
    return this.request('/gallery', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  async deleteGalleryItem(itemId) {
    return this.request(`/gallery/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Admin - Inventory
  async getInventory() {
    return this.request('/inventory');
  }

  async updateInventoryItem(itemId, updates) {
    return this.request(`/inventory/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Admin - Chat
  async getAdminChatRooms(adminEmail) {
    return this.request(`/chat/rooms?admin_email=${adminEmail}`);
  }

  async markMessagesRead(roomId) {
    return this.request(`/chat/rooms/${roomId}/read`, {
      method: 'PUT',
    });
  }

  // Analytics
  async getAnalytics(adminEmail, startDate, endDate) {
    return this.request(`/analytics?admin_email=${adminEmail}&start_date=${startDate}&end_date=${endDate}`);
  }
}

export const api = new ApiClient();
export default api;
